import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import axiosInstance, { weburl } from "../../../axiosInstance";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";
import { applicationStatusOptions } from "../../../helpers/jobPortalMeta";
import { normalizeResumeMode } from "../../../helpers/jobSeekerResume";

const AdminApplicationsPage = () => {
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState([]);
  const [summary, setSummary] = useState({});
  const [recruiters, setRecruiters] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [recruiterFilter, setRecruiterFilter] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", "10");
      if (searchTerm.trim()) params.set("search", searchTerm.trim());
      if (statusFilter !== "") params.set("status", String(statusFilter));
      if (recruiterFilter) params.set("recruiterId", recruiterFilter);

      const [applicationsResponse, recruitersResponse] = await Promise.all([
        axiosInstance.get(`/job-portal/admin/applications?${params.toString()}`, authConfig),
        axiosInstance.get("/job-portal/admin/users?role=recruiter&limit=100", authConfig),
      ]);

      const nextApplications = applicationsResponse.data?.data?.applications || [];
      setApplications(nextApplications);
      setSummary(applicationsResponse.data?.data?.summary || {});
      setMeta(applicationsResponse.data?.meta || { total: 0, totalPages: 1 });
      setRecruiters(recruitersResponse.data?.data?.users || []);
      setDrafts(
        nextApplications.reduce((acc, application) => {
          acc[application.id] = {
            status: application.status,
            note: application.internalNotes || application.recruiterNotes || "",
          };
          return acc;
        }, {})
      );
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, [page, statusFilter, recruiterFilter, searchTerm]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
  };

  const saveApplication = async (applicationId) => {
    try {
      const authConfig = await getJobPortalAuthConfig();
      const draft = drafts[applicationId];
      await axiosInstance.put(
        `/job-portal/applications/${applicationId}/status`,
        {
          status: Number(draft.status),
          internalNotes: draft.note,
          recruiterNotes: draft.note,
          note: draft.note || "Updated by admin",
        },
        authConfig
      );
      toast.success("Application updated");
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update application");
    }
  };

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Applications</h4>
              <p className="text-muted small mb-0">
                Admin visibility across all applicants, jobs, recruiters, and application statuses.
              </p>
            </div>
            <div className="badge bg-light text-dark px-3 py-2">
              Total: {meta.total || 0}
            </div>
          </div>

          <div className="row g-3 mb-4">
            {[
              { label: "Applied", value: summary.applied || 0 },
              { label: "Under Review", value: summary.underReview || 0 },
              { label: "Shortlisted", value: summary.shortlisted || 0 },
              { label: "Selected", value: summary.selected || 0 },
              { label: "Rejected", value: summary.rejected || 0 },
            ].map((card) => (
              <div className="col-md" key={card.label}>
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="small text-muted">{card.label}</div>
                    <div className="fs-4 fw-bold">{loading ? "--" : card.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body">
              <form className="row g-3" onSubmit={handleSearchSubmit}>
                <div className="col-md-5">
                  <input
                    className="form-control"
                    placeholder="Search by applicant, job, company, or location"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={recruiterFilter}
                    onChange={(e) => {
                      setRecruiterFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Recruiters</option>
                    {recruiters.map((recruiter) => (
                      <option key={recruiter.id} value={recruiter.id}>
                        {recruiter.name || recruiter.email}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPage(1);
                    }}
                  >
                    {applicationStatusOptions.map((option) => (
                      <option key={option.label} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <button className="btn btn-primary w-100" type="submit">
                    Search
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Applicant</th>
                    <th>Job</th>
                    <th>Recruiter</th>
                    <th>Status</th>
                    <th>Notes</th>
                    <th>Applied</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5 text-muted">
                        Loading applications...
                      </td>
                    </tr>
                  ) : applications.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-5 text-muted">
                        No applications found.
                      </td>
                    </tr>
                  ) : (
                    applications.map((application) => {
                      const draft = drafts[application.id] || {};
                      const resumeMode = normalizeResumeMode(
                        application.applicant?.resumeMode,
                        Boolean(application.applicant?.resumeUrl)
                      );
                      const resumeLink = application.applicant?.resumeUrl?.startsWith("public")
                        ? `${weburl}${application.applicant.resumeUrl.replace("public", "")}`
                        : application.applicant?.resumeUrl || "";

                      return (
                        <tr key={application.id}>
                          <td>
                            <div className="fw-semibold">{application.applicant?.name || "-"}</div>
                            <div className="text-muted small">
                              {application.applicant?.email || "-"}
                            </div>
                          </td>
                          <td>
                            <div>{application.job?.title || "-"}</div>
                            <div className="text-muted small">
                              {application.job?.location?.display || "-"}
                            </div>
                          </td>
                          <td>{application.job?.recruiter?.name || "-"}</td>
                          <td style={{ minWidth: 170 }}>
                            <select
                              className="form-select form-select-sm"
                              value={draft.status ?? application.status}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [application.id]: {
                                    ...prev[application.id],
                                    status: e.target.value,
                                  },
                                }))
                              }
                            >
                              {applicationStatusOptions
                                .filter((option) => option.value !== "")
                                .map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                            </select>
                          </td>
                          <td style={{ minWidth: 220 }}>
                            <input
                              className="form-control form-control-sm"
                              placeholder="Admin note"
                              value={draft.note || ""}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [application.id]: {
                                    ...prev[application.id],
                                    note: e.target.value,
                                  },
                                }))
                              }
                            />
                          </td>
                          <td>
                            {application.appliedAt
                              ? new Date(application.appliedAt).toLocaleDateString("en-IN")
                              : "-"}
                          </td>
                          <td>
                            <div className="d-flex gap-2 flex-wrap">
                              <Link
                                to={`/user-job-seeker/${application.applicantId}`}
                                className="btn btn-sm btn-outline-primary"
                              >
                                Candidate
                              </Link>
                              <Link
                                to={`/job-view/${application.jobId}`}
                                className="btn btn-sm btn-outline-dark"
                              >
                                Job
                              </Link>
                              {resumeMode === "upload" && resumeLink ? (
                                <a
                                  href={resumeLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="btn btn-sm btn-outline-secondary"
                                >
                                  Resume
                                </a>
                              ) : null}
                              {(resumeMode === "auto" || !resumeLink) ? (
                                <Link
                                  to={`/user-job-seeker/${application.applicantId}#generated-resume`}
                                  className="btn btn-sm btn-outline-secondary"
                                >
                                  Generated Resume
                                </Link>
                              ) : null}
                              <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={() => saveApplication(application.id)}
                              >
                                Save
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="card-body border-top d-flex justify-content-between align-items-center">
              <span className="text-muted small">
                Page {page} of {meta.totalPages || 1}
              </span>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => prev - 1)}
                >
                  Previous
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page >= (meta.totalPages || 1)}
                  onClick={() => setPage((prev) => prev + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default AdminApplicationsPage;
